// Command core (ARCH UI-5, D16). PURE dispatch over a CommandContext the host
// (client/server/test) implements. Cheats-gated; parse errors become results,
// never throws (E-23). The command set doubles as the E2E/scripting harness.
import { tokenize, ArgReader, CommandError } from './parser';
import { heredityRuleMessage, HEREDITY_RULE, PACE_RULE } from '../../heredity/rule';

/** The gamerules `/gamerule ` completes even before a world has set them — every rule
 *  the engine honours. A host adds whatever else its world has set on top. */
export const KNOWN_GAMERULES: readonly string[] = [
  'keepInventory', 'doDaylightCycle', 'doWeatherCycle', 'mobGriefing', 'randomTickSpeed', 'doMobSpawning',
  // §28.2 (HER-1): both heredity rules, so `/gamerule ` completes them.
  HEREDITY_RULE, PACE_RULE,
];

export interface CommandContext {
  readonly cheats: boolean;
  hasBlock(name: string): boolean;
  blockNames(): string[];
  /** All item names, for /give completion + validation (M4). */
  itemNames(): string[];
  getBlock(x: number, y: number, z: number): string;
  setBlock(x: number, y: number, z: number, block: string): void;
  fillBlocks(x1: number, y1: number, z1: number, x2: number, y2: number, z2: number, block: string): number;
  /** Add `count` of `item` to the player inventory; returns true if the item exists. */
  giveItem(item: string, count: number): boolean;
  getPos(): { x: number; y: number; z: number };
  tp(x: number, y: number, z: number): void;
  getGamemode(): string;
  setGamemode(mode: string): void;
  getSeed(): string;
  getTime(): number;
  setTime(t: number): void;
  /** Set the weather (PLR-10); `durationSeconds` optional. */
  setWeather(kind: 'clear' | 'rain' | 'thunder', durationSeconds?: number): void;
  getGamerule(name: string): string | undefined;
  setGamerule(name: string, value: string): void;
  listGamerules(): string[];
  /** Spawnable mob names, for /summon completion + validation (M5). */
  mobNames(): string[];
  /** Spawn a mob at (x,y,z); returns true if the type exists. */
  summon(mob: string, x: number, y: number, z: number): boolean;
  /** Remove all loaded mobs; returns the number killed. */
  killEntities(): number;
  /** Locatable structure type names, for /locate completion (M6.3). */
  structureTypes(): string[];
  /** Nearest structure of `type` to (fromX,fromZ) — world block coords, or null. */
  locate(type: string, fromX: number, fromZ: number): { x: number; z: number } | null;
}

export interface CommandResult {
  ok: boolean;
  message: string;
}

interface CommandDef {
  run(ctx: CommandContext, args: ArgReader): string;
  /** One-line usage + description for /help. */
  help: string;
  /** Runnable even when cheats are disabled (informational commands, e.g. /help). */
  noCheat?: boolean;
  /** Suggestions for the 0-based argument index currently being typed. */
  complete?(ctx: CommandContext, argIndex: number): string[];
}

const GAMEMODES = ['survival', 'creative', 'adventure', 'spectator'] as const;
const TIME_KEYWORDS: Record<string, number> = { day: 1000, noon: 6000, night: 13000, midnight: 18000 };
const MAX_FILL = 32 * 32 * 32; // 32768 (ARCH: /fill ≤ 32³)

const COMMANDS: Record<string, CommandDef> = {
  help: {
    help: '/help — list the available commands',
    noCheat: true, // informational: usable even when cheats are off
    run(ctx, a) {
      a.end();
      const lines = ['Commands:'];
      for (const name of Object.keys(COMMANDS).sort()) lines.push(`  ${COMMANDS[name]!.help}`);
      if (!ctx.cheats) lines.push('(cheats are off in this world — enable them in the pause menu to use these)');
      return lines.join('\n');
    },
  },
  gamemode: {
    help: '/gamemode <survival|creative|adventure|spectator>',
    run(ctx, a) {
      const mode = a.choice(GAMEMODES, 'game mode');
      a.end();
      ctx.setGamemode(mode);
      return `Set game mode to ${mode}`;
    },
    complete: (_c, i) => (i === 0 ? [...GAMEMODES] : []),
  },
  tp: {
    help: '/tp <x> <y> <z> — teleport (~ = relative)',
    run(ctx, a) {
      const p = ctx.getPos();
      const x = a.posCoord(p.x), y = a.posCoord(p.y), z = a.posCoord(p.z);
      a.end();
      ctx.tp(x, y, z);
      return `Teleported to ${fmt(x)}, ${fmt(y)}, ${fmt(z)}`;
    },
  },
  setblock: {
    help: '/setblock <x> <y> <z> <block>',
    run(ctx, a) {
      const p = ctx.getPos();
      const x = a.blockCoord(p.x), y = a.blockCoord(p.y), z = a.blockCoord(p.z);
      const block = a.block();
      a.end();
      ctx.setBlock(x, y, z, block);
      return `Set ${block} at ${x} ${y} ${z}`;
    },
    complete: (ctx, i) => (i === 3 ? ctx.blockNames() : []),
  },
  fill: {
    help: '/fill <x1 y1 z1> <x2 y2 z2> <block>',
    run(ctx, a) {
      const p = ctx.getPos();
      const x1 = a.blockCoord(p.x), y1 = a.blockCoord(p.y), z1 = a.blockCoord(p.z);
      const x2 = a.blockCoord(p.x), y2 = a.blockCoord(p.y), z2 = a.blockCoord(p.z);
      const block = a.block();
      a.end();
      const vol = (Math.abs(x2 - x1) + 1) * (Math.abs(y2 - y1) + 1) * (Math.abs(z2 - z1) + 1);
      if (vol > MAX_FILL) throw new CommandError(`Too many blocks in fill: ${vol} (max ${MAX_FILL})`);
      const n = ctx.fillBlocks(x1, y1, z1, x2, y2, z2, block);
      return `Filled ${n} blocks with ${block}`;
    },
    complete: (ctx, i) => (i === 6 ? ctx.blockNames() : []),
  },
  time: {
    help: '/time <set|add|query> [value]',
    run(ctx, a) {
      const sub = a.choice(['set', 'add', 'query'], 'set|add|query');
      if (sub === 'query') {
        a.end();
        return `Time is ${ctx.getTime()}`;
      }
      const vt = a.word('time');
      const value = vt in TIME_KEYWORDS ? TIME_KEYWORDS[vt]! : Number(vt);
      if (!Number.isFinite(value)) throw new CommandError(`Invalid time '${vt}'`);
      a.end();
      ctx.setTime(sub === 'add' ? ctx.getTime() + value : value);
      return `${sub === 'add' ? 'Added' : 'Set'} time (now ${ctx.getTime()})`;
    },
    complete: (_c, i) => (i === 0 ? ['set', 'add', 'query'] : i === 1 ? Object.keys(TIME_KEYWORDS) : []),
  },
  weather: {
    help: '/weather <clear|rain|thunder> [seconds]',
    run(ctx, a) {
      const kind = a.choice(['clear', 'rain', 'thunder'], 'clear|rain|thunder') as 'clear' | 'rain' | 'thunder';
      const seconds = a.hasMore() ? a.int('seconds') : undefined;
      a.end();
      ctx.setWeather(kind, seconds);
      return `Set the weather to ${kind}`;
    },
    complete: (_c, i) => (i === 0 ? ['clear', 'rain', 'thunder'] : []),
  },
  seed: {
    help: '/seed — show the world seed',
    run(ctx, a) {
      a.end();
      return `Seed: ${ctx.getSeed()}`;
    },
  },
  gamerule: {
    help: '/gamerule <rule> [value] — query or set a game rule',
    run(ctx, a) {
      const name = a.word('rule');
      if (!a.hasMore()) {
        a.end();
        return `${name} = ${ctx.getGamerule(name) ?? '(unset)'}`;
      }
      const value = a.word('value');
      a.end();
      ctx.setGamerule(name, value);
      // §28.2: the two heredity rules answer in their own binding words.
      return heredityRuleMessage(name, value) ?? `Gamerule ${name} set to ${value}`;
    },
    complete: (ctx, i) => (i === 0 ? ctx.listGamerules() : []),
  },
  give: {
    help: '/give <item> [count]',
    run(ctx, a) {
      const item = a.word('item');
      const count = a.hasMore() ? Math.max(1, Math.floor(Number(a.word('count')))) : 1;
      a.end();
      if (!Number.isFinite(count)) throw new CommandError('Invalid count');
      if (!ctx.giveItem(item, count)) throw new CommandError(`Unknown item: ${item}`);
      return `Gave ${count} ${item}`;
    },
    complete: (ctx, i) => (i === 0 ? ctx.itemNames() : []),
  },
  summon: {
    help: '/summon <mob> [x y z] — spawn a mob (default: at you)',
    run(ctx, a) {
      const mob = a.word('mob');
      const p = ctx.getPos();
      let x = p.x, y = p.y, z = p.z;
      if (a.hasMore()) { x = a.posCoord(p.x); y = a.posCoord(p.y); z = a.posCoord(p.z); }
      a.end();
      if (!ctx.summon(mob, x, y, z)) throw new CommandError(`Unknown mob: ${mob}`);
      return `Summoned ${mob} at ${fmt(x)}, ${fmt(y)}, ${fmt(z)}`;
    },
    complete: (ctx, i) => (i === 0 ? ctx.mobNames() : []),
  },
  kill: {
    help: '/kill — remove all loaded mobs',
    run(ctx, a) {
      a.end();
      const n = ctx.killEntities();
      return n === 0 ? 'No mobs to remove' : `Removed ${n} mob${n === 1 ? '' : 's'}`;
    },
  },
  locate: {
    help: '/locate <structure> — find the nearest structure',
    run(ctx, a) {
      const type = a.word('structure');
      a.end();
      if (!ctx.structureTypes().includes(type)) throw new CommandError(`Unknown structure: ${type}`);
      const p = ctx.getPos();
      const found = ctx.locate(type, p.x, p.z);
      if (!found) return `Could not find a ${type} nearby`;
      const dist = Math.round(Math.hypot(found.x - p.x, found.z - p.z));
      return `The nearest ${type} is at [${found.x}, ~, ${found.z}] (${dist} blocks away)`;
    },
    complete: (ctx, i) => (i === 0 ? ctx.structureTypes() : []),
  },
};

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export function commandNames(): string[] {
  return Object.keys(COMMANDS);
}

/** One-line usage strings for every command (Help screen / `/help`). */
export function commandHelpLines(): string[] {
  return Object.keys(COMMANDS).sort().map((n) => COMMANDS[n]!.help);
}

export function executeCommand(ctx: CommandContext, input: string): CommandResult {
  const raw = input.trim().replace(/^\//, '');
  const toks = tokenize(raw);
  if (toks.length === 0) return { ok: false, message: 'Empty command' };
  const name = toks[0]!.text;
  const def = COMMANDS[name];
  if (!def) return { ok: false, message: `Unknown command: ${name}` };
  if (!ctx.cheats && !def.noCheat) return { ok: false, message: 'Cheats are disabled in this world' };
  const reader = new ArgReader(toks.slice(1), ctx);
  try {
    return { ok: true, message: def.run(ctx, reader) };
  } catch (e) {
    if (e instanceof CommandError) {
      return { ok: false, message: e.pos != null ? `${e.message} (at position ${e.pos})` : e.message };
    }
    return { ok: false, message: `Error: ${(e as Error).message}` };
  }
}

/** Tab-completion suggestions for the current input. */
export function completeCommand(ctx: CommandContext, input: string): string[] {
  const raw = input.replace(/^\//, '');
  const toks = tokenize(raw);
  const trailingSpace = raw.endsWith(' ');
  if (toks.length === 0 || (toks.length === 1 && !trailingSpace)) {
    const prefix = toks[0]?.text ?? '';
    return commandNames().filter((c) => c.startsWith(prefix));
  }
  const def = COMMANDS[toks[0]!.text];
  if (!def?.complete) return [];
  const argIndex = trailingSpace ? toks.length - 1 : toks.length - 2;
  const partial = trailingSpace ? '' : toks[toks.length - 1]!.text;
  return def.complete(ctx, argIndex).filter((s) => s.startsWith(partial));
}
